import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NotificationDeliveryType } from '@app/model/enums/notification-delivery-type.enum';
import { NotificationDelivery } from '@app/model/notification-delivery';
import { NotificationApiService } from '@app/services/api/notification-api.service';
import { NavigateService } from '@app/services/navigate.service';
import { NotificationService } from '@app/services/notification.service';
import { NgScrollbar } from 'ngx-scrollbar';
import { fromEvent, Subject, timer } from 'rxjs';
import { filter, finalize, switchMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-notifications-list-dialog',
    templateUrl: './notifications-list-dialog.component.html',
    styleUrls: ['./notifications-list-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NotificationsListDialogComponent implements OnInit, OnDestroy {
  @ViewChild(NgScrollbar, { static: false }) public scrollbarRef: NgScrollbar;

  public notifications: Array<NotificationDelivery> = [];

  public placeholders = new Array(4).fill(null);

  public unreadNotificationsCount = 0;

  public hasNextNotifications = false;
  public isLoading = false;
  private currentPage = 1;
  private unsubscribe: Subject<void> = new Subject();

  private initializedData =  false;
  
  constructor(
    private readonly notificationApiService: NotificationApiService,
    private readonly navigationService: NavigateService,
    private readonly elementRef: ElementRef,
    private readonly notificationService: NotificationService) { }

  ngOnInit() {
    this.addSubscriptionForNotifications();
    this.listenForOutsideClicks();

    this.loadNotifications();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public notificationNavigate(item: NotificationDelivery): void {
    this.notificationService.markNotificationAsSeen(item, NotificationDeliveryType.REGULAR);
    this.navigationService.navigateByNavigationRouteLink(item.link);
  }

  public closeDialog(): void {
    this.notificationService.closeListDialog();
  }

  public onNotificationsScroll(): void {
    if (this.isLoading || !this.hasNextNotifications) {
      return;
    }

    const el = this.scrollbarRef.adapter.viewportElement;

    const distanceToBottom =
      el.scrollHeight - el.offsetHeight - el.scrollTop;

    if (distanceToBottom < 100) {
      this.loadNotifications(this.currentPage + 1);
    }
  }

  private loadNotifications(page = 1): void {
    this.isLoading = true;

    this.notificationApiService.getNotifications(page)
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe(data => {
        this.currentPage = page;
        this.hasNextNotifications = !!data?.['hydra:view']?.['hydra:next'];

        this.prependNotificationsToList(data?.['hydra:member']);
        this.initializedData = true;
      });
  }

  private prependNotificationsToList(items: NotificationDelivery[], prepend = false): void {
    if (!items) {
      return;
    }
  
    const uniqueNotifications = items.filter(item => !this.notifications.some(notification => notification.notificationId === item.notificationId));

    if (!prepend) {
      this.notifications.push(...uniqueNotifications)
      return;
    }

    this.notifications.unshift(...uniqueNotifications);
  }

  private listenForOutsideClicks(): void {
    timer(100)
      .pipe(
        switchMap(() => fromEvent(document, 'click')),
        takeUntil(this.unsubscribe),
        filter((event: MouseEvent) => {
          return !this.elementRef.nativeElement.contains(event.target);
        })
      )
      .subscribe(() => {
        this.closeDialog();
      });
  }

  private addSubscriptionForNotifications(): void {
    this.notificationService.unreadNotificationsCount
      .pipe(
        takeUntil(this.unsubscribe)
      )
      .subscribe(data => {
        this.unreadNotificationsCount = data;
      });

    this.notificationService.notifications
      .pipe(
        takeUntil(this.unsubscribe)
      )
      .subscribe(data => {
        if (!this.initializedData) {
          return;
        }
        
        this.prependNotificationsToList(data, true);
      });
  }
}
